import React from "react";
import { Select, Spin, Empty } from "antd";
import { useServices } from "../../context/ServiceProvider";

// Reusable remote-search lookup component.
// - Displays an Ant Design Select with server-side search
// - Stores only the selected record Id as the value
// - Accepts an objectType to resolve which service to use for searching

export default function SearchableLookup({
  objectType,
  searchParam,
  value,
  valueLabel,
  onChange,
  placeholder = "Search...",
  minSearchChars = 3,
  disabled = false,
  className = "w-full",
  style,
}) {
  const services = useServices();
  const [options, setOptions] = React.useState([]);
  const [fetching, setFetching] = React.useState(false);
  const [searchTerm, setSearchTerm] = React.useState("");
  const debounceRef = React.useRef(null);

  const resolveSearchFn = React.useCallback(() => {
    if (!services) return null;
    if (objectType === "Project") return services.project.searchProjects;
    if (objectType === "ProjectObjective")
      return services.projectObjective.searchProjectObjectives;
    if (objectType === "QualificationStep")
      return services.projectQualStep.searchQualSteps;
    if (objectType === "ProjectQualificationStep")
      return services.projectQualStep.searchQualSteps;
    if (objectType === "ProjectPage")
      return services.projectPage.searchProjectPages;
    if (objectType === "ProjectPageComponent")
      return services.projectPageComponent.searchProjectPageComponents;
    if (objectType === "Account") return services.account.searchAccounts;
    if (objectType === "Contributor")
      return services.contributor.searchContributors;
    if (objectType === "User") return services.user.searchUsers;
    return null;
  }, [objectType, services]);

  const searchFn = resolveSearchFn();

  React.useEffect(() => {
    const term = (searchTerm || "").trim();
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    if (!searchFn || term.length < minSearchChars) {
      setFetching(false);
      setOptions([]);
      return;
    }
    setFetching(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const { projectId, projectObjectiveId } = searchParam;
        const results = await searchFn(term, projectId, projectObjectiveId);
        const list = Array.isArray(results) ? results : [];
        let mapped = [];
        if (objectType === "QualificationStep") {
          const dedup = new Map();
          list.forEach((rec) => {
            const id =
              rec?.Qualification_Step__c ||
              rec?.Qualification_Step__r?.Id ||
              rec?.qualificationStepId ||
              rec?.Qualification_Step_Id__c;
            const name = rec?.Qualification_Step__r?.Name || id;
            if (id && !dedup.has(id)) {
              dedup.set(id, { value: id, label: name, record: rec });
            }
          });
          mapped = Array.from(dedup.values());
        } else {
          mapped = list.map((rec) => {
            const id = rec.Id || rec.id;
            const name = (rec && rec?.Name) || rec?.name || id;
            return { value: id, label: name, record: rec };
          });
        }
        setOptions(mapped);
      } finally {
        setFetching(false);
      }
    }, 400);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchTerm, searchParam, searchFn, minSearchChars, objectType]);

  // Ensure the currently selected value appears in the dropdown label list even if not in options
  const computedOptions = React.useMemo(() => {
    if (!value || !valueLabel) return options;
    const exists = options.some((opt) => String(opt.value) === String(value));
    if (exists) return options;
    return [{ value, label: valueLabel }, ...options];
  }, [options, value, valueLabel]);

  return (
    <Select
      showSearch
      allowClear
      disabled={disabled}
      className={className}
      style={style}
      placeholder={placeholder}
      value={value || undefined}
      options={computedOptions}
      filterOption={false}
      onSearch={setSearchTerm}
      onChange={(val, opt) => onChange(val || "", opt?.label || "")}
      notFoundContent={
        fetching ? (
          <div className="px-3 py-2 text-sm text-gray-500">
            <Spin size="small" /> Searching…
          </div>
        ) : (searchTerm || "").trim().length < minSearchChars ? (
          <div className="px-3 py-2 text-sm text-gray-500">
            Enter at least {minSearchChars} characters to search.
          </div>
        ) : (
          <div className="px-3 py-2">
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="No matches"
            />
          </div>
        )
      }
    />
  );
}
